import type { JSX, ReactNode } from 'react';
import { cx } from './primitives';
import type { TableState } from './table-state';

/**
 * A column heading that is also the control that sorts by it.
 *
 * The whole heading is the button, so the target is the width of the column
 * rather than a glyph beside it, and `aria-sort` on the `<th>` tells a screen
 * reader which column orders the table - the arrows are decoration and hidden.
 *
 * Both arrows are always drawn, one of them lit. A single arrow on the active
 * column says nothing about the columns that are ALSO sortable, which is the
 * question a reader has before they click anything; the faint pair is the
 * affordance. The same stacked pair the smb-voice Factures table draws.
 *
 * The button's text is the label and nothing else - the marks are an SVG with
 * no text nodes - because `TableFrame` mirrors this cell's `textContent` onto
 * every cell below it as the card label on a phone. The label sits in a span
 * for that same reason it must not be a bare text node: see below.
 */
export interface SortHeaderProps<Row, Key extends string> {
  readonly table: Pick<TableState<Row, Key>, 'sort' | 'sortBy'>;
  readonly column: Key;
  /** A column of figures: the heading sits at the end edge, over them. */
  readonly numeric?: boolean;
  readonly className?: string;
  /** Already translated. The label and nothing else. */
  readonly children: ReactNode;
}

export function SortHeader<Row, Key extends string>({
  table,
  column,
  numeric = false,
  className,
  children,
}: SortHeaderProps<Row, Key>): JSX.Element {
  const active = table.sort !== null && table.sort.key === column;
  const ascending = active && table.sort?.direction === 'asc';
  const descending = active && table.sort?.direction === 'desc';
  return (
    <th
      scope="col"
      className={cx(numeric && 'cib-cell--num', className)}
      aria-sort={ascending ? 'ascending' : descending ? 'descending' : 'none'}
    >
      <button type="button" className="cib-sort" onClick={() => table.sortBy(column)}>
        {/* The label is its own box so it can be the thing that truncates. Left
            as a bare text node it is an anonymous flex item, which does not
            shrink - and in a numeric column, where the row is packed to the end
            edge, what overflows is then the START of the word and is cut with
            no ellipsis to say so: the pending payouts table printed
            "DJUSTMENTS" at a 939px container (pull request 922, before this
            heading was shared). It still contributes its text, which is what
            `TableFrame` reads for the card label on a phone, and it is where a
            fitted column's heading clips - `titleClippedCells` walks
            descendants, so the whole word comes back on the cell's `title`. */}
        <span className="cib-sort__label">{children}</span>
        <svg className="cib-sort__marks" viewBox="0 0 10 16" aria-hidden="true" focusable="false">
          <path
            className={cx('cib-sort__up', ascending && 'cib-sort__mark--active')}
            d="M2 6.5 L5 3.5 L8 6.5"
          />
          <path
            className={cx('cib-sort__down', descending && 'cib-sort__mark--active')}
            d="M2 9.5 L5 12.5 L8 9.5"
          />
        </svg>
      </button>
    </th>
  );
}
